import { first, pickBy } from 'shared/utils/misc';
import { getExternalContext } from 'shared/utils/placement';
import { ParsedURL } from './ParsedURL';
// These query parameters will be passed through to
// the click URL destination, along with any UTM values.
var PASS_THROUGH_EXTERNAL_CONTEXT = [
    'idCreativeResource',
    'site_id',
    'tid',
    'uid',
];
/**
 * Add tracking and pass-through query params to the click URL.
 * These are provided via the window/iframe URL query parameters
 * or `window.ADAPT.externalContext` object.  Non-http/https URLs are left
 * un-modified, since some ad platforms do not support tracking them.
 *
 * 1. replace `[RANDOM]` with actual random value
 * 2. inherit `site_id` from iframe url
 * 3. inherit utm values from iframe url
 * 4. Attach query params to the click URL.
 * 5. If there is a custom `turl` from the iframe url, encode the
 *    final click URL and pass it as a param to that `turl`.
 *
 * NOTE: the inherited params prioritize click tag URL over iframe URL
 */
export var processClickTagUrl = function (clickUrl) {
    if (!/^https?/.test(clickUrl)) {
        return clickUrl;
    }
    var externalParams = getExternalContext();
    var finalClickUrl = buildFinalDestinationUrl(clickUrl, externalParams);
    /**
     * `turl` is a click-tracking URL that comes from the ad delivery network.
     */
    var turl = validUrlOrUndefined(first(externalParams.turl));
    return turl ? "" + turl + encodeURIComponent(finalClickUrl) : finalClickUrl;
};
/**
 * Append utm_values, itm_values, pass-through
 * query parameters, etc. to the final destination URL.
 */
var buildFinalDestinationUrl = function (clickUrl, externalParams) {
    // replace RANDOM to cache buster
    var cacheBustedUrl = clickUrl.replace(/\[RANDOM\]/, "" + Math.random());
    var parsedURL = new ParsedURL(cacheBustedUrl);
    var filteredExternalParams = pickBy(function (_, key) {
        return PASS_THROUGH_EXTERNAL_CONTEXT.indexOf(key) !== -1 ||
            key.toLowerCase().indexOf('utm_') === 0 ||
            key.toLowerCase().indexOf('itm_') === 0;
    }, externalParams);
    var ctParam = parsedURL.queryParams.ct;
    // Default UTMs were probably disabled for good reason here.
    // Most likely, a clients web server has a poor query param
    // implementation. Therefore, the URL should be left alone.
    if (Object.keys(filteredExternalParams).length === 0) {
        return cacheBustedUrl;
    }
    if (ctParam && typeof ctParam === 'string') {
        // Append the UTMs, etc to the `ct` query param if it exists.
        var ct = ParsedURL.of(ctParam);
        ct.mergeUndefinedParams(filteredExternalParams);
        parsedURL.queryParams.ct = ct.href();
    }
    else {
        // Otherwise, append it to the provided `clickUrl`.
        parsedURL.mergeUndefinedParams(filteredExternalParams);
    }
    return parsedURL.href();
};
/**
 * Returns the provided string as-is if it is a
 * valid URL. Returns undefined if it is not.
 */
var URL_REGEX = new RegExp('^https?:\\/\\/');
export var validUrlOrUndefined = function (possibleUrl) {
    return possibleUrl && URL_REGEX.test(possibleUrl) ? possibleUrl : undefined;
};
